import { appPrompt } from '../../services/appDialogs';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Pencil, PenTool, Brush, Highlighter, Minus, Square, Circle,
  Eraser, Type, Pipette, Undo2, Redo2, Trash2, X, MousePointer,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { uploadBase64ToCos } from '../../utils/fileUtils';

// ── 笔刷类型定义 ──
// brush: 'pen' | 'pencil' | 'ink' | 'marker' | 'watercolor'
// 每种笔刷对压感的响应不同
const BRUSH_TYPES = [
  { id: 'pen',        label: '圆珠笔', icon: Pencil,      desc: '均匀线条' },
  { id: 'pencil',     label: '铅笔',   icon: PenTool,     desc: '带纹理感' },
  { id: 'ink',        label: '钢笔',   icon: Brush,       desc: '压感粗细' },
  { id: 'marker',     label: '马克笔', icon: Highlighter,  desc: '半透明叠色' },
];

const TOOLS = [
  { id: 'select',     icon: MousePointer, label: '选择' },
  { id: 'brush',      icon: Pencil,       label: '画笔' },  // 展开笔刷子菜单
  { id: 'line',       icon: Minus,        label: '直线' },
  { id: 'rect',       icon: Square,       label: '矩形' },
  { id: 'circle',     icon: Circle,       label: '圆形' },
  { id: 'eraser',     icon: Eraser,       label: '橡皮擦' },
  { id: 'text',       icon: Type,         label: '文字' },
  { id: 'eyedropper', icon: Pipette,      label: '吸色' },
];

const CANVAS_SIZES = {
  '16:9': { w: 1920, h: 1080 },
  '9:16': { w: 1080, h: 1920 },
  '1:1':  { w: 1080, h: 1080 },
  '4:3':  { w: 1440, h: 1080 },
  '3:4':  { w: 1080, h: 1440 },
};

const MAX_HISTORY = 30;

const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:3', '3:4'];

const SketchEditorModal = ({ isOpen, aspectRatio: initialAspectRatio = '16:9', onSave, onClose, title }) => {
  const { getTheme } = useTheme();
  const theme = getTheme();

  const canvasRef = useRef(null);
  const overlayRef = useRef(null);
  const [tool, setTool] = useState('brush');
  const [brushType, setBrushType] = useState('pen');  // pen | pencil | ink | marker
  const [color, setColor] = useState('#000000'); // 画布笔刷色（用户绘制色，与主题无关，保留）
  const [brushSize, setBrushSize] = useState(4);
  const [fillShape, setFillShape] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(initialAspectRatio);
  const [showBrushMenu, setShowBrushMenu] = useState(false);

  // 历史记录
  const historyRef = useRef([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // 绘制状态
  const drawing = useRef(false);
  const lastPos = useRef({ x: 0, y: 0 });
  const startPos = useRef({ x: 0, y: 0 });
  const lastPressure = useRef(0.5);

  // 打开时重置比例
  useEffect(() => {
    if (isOpen) setAspectRatio(initialAspectRatio);
  }, [isOpen, initialAspectRatio]);

  const size = CANVAS_SIZES[aspectRatio] || CANVAS_SIZES['16:9'];

  // 计算显示尺寸（适配屏幕）
  const getDisplaySize = useCallback(() => {
    const maxW = window.innerWidth * 0.75;
    const maxH = window.innerHeight * 0.72;
    const scale = Math.min(maxW / size.w, maxH / size.h, 1);
    return { w: Math.round(size.w * scale), h: Math.round(size.h * scale), scale };
  }, [size]);

  const [displaySize, setDisplaySize] = useState(() => getDisplaySize());

  useEffect(() => {
    const handleResize = () => setDisplaySize(getDisplaySize());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getDisplaySize]);

  // 初始化画布
  useEffect(() => {
    if (!isOpen) return;
    setDisplaySize(getDisplaySize());

    const initCanvas = () => {
      const canvas = canvasRef.current;
      const overlay = overlayRef.current;
      if (!canvas || !overlay) return;

      canvas.width = size.w;
      canvas.height = size.h;
      overlay.width = size.w;
      overlay.height = size.h;

      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(0, 0, size.w, size.h);

      // 初始化历史
      historyRef.current = [ctx.getImageData(0, 0, size.w, size.h)];
      historyIndexRef.current = 0;
      setCanUndo(false);
      setCanRedo(false);
    };

    // 延迟确保 DOM 已渲染
    requestAnimationFrame(initCanvas);
  }, [isOpen, size, getDisplaySize]);

  // 保存快照
  const saveSnapshot = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const snap = ctx.getImageData(0, 0, canvas.width, canvas.height);

    const idx = historyIndexRef.current;
    historyRef.current = historyRef.current.slice(0, idx + 1);
    historyRef.current.push(snap);
    if (historyRef.current.length > MAX_HISTORY) historyRef.current.shift();
    historyIndexRef.current = historyRef.current.length - 1;

    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
  }, []);

  const undo = useCallback(() => {
    if (historyIndexRef.current <= 0) return;
    historyIndexRef.current--;
    const canvas = canvasRef.current;
    canvas.getContext('2d').putImageData(historyRef.current[historyIndexRef.current], 0, 0);
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(true);
  }, []);

  const redo = useCallback(() => {
    if (historyIndexRef.current >= historyRef.current.length - 1) return;
    historyIndexRef.current++;
    const canvas = canvasRef.current;
    canvas.getContext('2d').putImageData(historyRef.current[historyIndexRef.current], 0, 0);
    setCanUndo(true);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  }, []);

  const getCanvasPos = useCallback((e) => {
    const overlay = overlayRef.current;
    if (!overlay) return { x: 0, y: 0 };
    const rect = overlay.getBoundingClientRect();
    const scaleX = size.w / rect.width;
    const scaleY = size.h / rect.height;
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  }, [size]);

  // ── 压感笔刷绘制引擎 ──
  // 在两点之间画一系列圆点（stamp），根据压感调整半径和透明度
  const stampBrush = useCallback((ctx, from, to, pressure, type, sz, clr) => {
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    const p = Math.max(0.1, pressure); // 鼠标无压感时 pressure=0.5

    // 各笔刷的压感映射
    let radius, alpha, spacing;
    switch (type) {
      case 'pencil': {
        // 铅笔：压感影响透明度，线条带颗粒感
        radius = sz * 0.5;
        alpha = 0.15 + p * 0.45;
        spacing = Math.max(1, radius * 0.3);
        break;
      }
      case 'ink': {
        // 钢笔：压感强烈影响粗细，线条锐利
        radius = sz * (0.2 + p * 1.2);
        alpha = 0.85 + p * 0.15;
        spacing = Math.max(1, radius * 0.25);
        break;
      }
      case 'marker': {
        // 马克笔：固定粗细，半透明叠色
        radius = sz * 1.2;
        alpha = 0.08 + p * 0.12;
        spacing = Math.max(1, radius * 0.4);
        break;
      }
      default: { // pen — 圆珠笔：均匀线条，压感轻微影响粗细
        radius = sz * (0.7 + p * 0.3);
        alpha = 0.9;
        spacing = Math.max(1, radius * 0.25);
        break;
      }
    }

    const steps = Math.max(1, Math.ceil(dist / spacing));
    ctx.save();
    ctx.globalCompositeOperation = 'source-over';

    // 解析颜色
    const hex = clr.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    for (let i = 0; i <= steps; i++) {
      const t = steps === 0 ? 0 : i / steps;
      const x = from.x + (to.x - from.x) * t;
      const y = from.y + (to.y - from.y) * t;

      if (type === 'pencil') {
        // 铅笔：用多个小点模拟颗粒纹理
        const grainCount = Math.ceil(radius * 1.5);
        for (let g2 = 0; g2 < grainCount; g2++) {
          const angle = Math.random() * Math.PI * 2;
          const rd = Math.random() * radius;
          const gx = x + Math.cos(angle) * rd;
          const gy = y + Math.sin(angle) * rd;
          const ga = alpha * (0.3 + Math.random() * 0.7);
          ctx.fillStyle = `rgba(${r},${g},${b},${ga})`;
          ctx.fillRect(gx, gy, 1, 1);
        }
      } else {
        ctx.beginPath();
        ctx.arc(x, y, Math.max(0.5, radius), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`;
        ctx.fill();
      }
    }
    ctx.restore();
  }, []);

  // 绘制逻辑
  const handlePointerDown = useCallback(async (e) => {
    const pos = getCanvasPos(e);
    drawing.current = true;
    startPos.current = pos;
    lastPos.current = pos;
    lastPressure.current = e.pressure > 0 ? e.pressure : 0.5;

    if (tool === 'eyedropper') {
      const ctx = canvasRef.current.getContext('2d');
      const pixel = ctx.getImageData(Math.round(pos.x), Math.round(pos.y), 1, 1).data;
      const hex = '#' + [pixel[0], pixel[1], pixel[2]].map(v => v.toString(16).padStart(2, '0')).join('');
      setColor(hex);
      setTool('brush');
      drawing.current = false;
      return;
    }

    if (tool === 'text') {
      // Unlike a native prompt, the app dialog does not block pointer events.
      // Keep the canvas idle and abandon the edit if its modal was unmounted.
      drawing.current = false;
      const canvas = canvasRef.current;
      const text = (await appPrompt('输入文字'));
      if (!text || !canvas || canvasRef.current !== canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = color;
      ctx.font = `${brushSize * 4}px sans-serif`;
      ctx.fillText(text, pos.x, pos.y);
      saveSnapshot();
      drawing.current = false;
      return;
    }

    if (tool === 'brush') {
      // 落笔时画一个点
      const ctx = canvasRef.current.getContext('2d');
      stampBrush(ctx, pos, pos, lastPressure.current, brushType, brushSize, color);
    }

    if (tool === 'eraser') {
      const ctx = canvasRef.current.getContext('2d');
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
    }
  }, [tool, brushType, color, brushSize, getCanvasPos, saveSnapshot, stampBrush]);

  const handlePointerMove = useCallback((e) => {
    if (!drawing.current) return;
    const pos = getCanvasPos(e);
    const pressure = e.pressure > 0 ? e.pressure : 0.5;

    if (tool === 'brush') {
      const ctx = canvasRef.current.getContext('2d');
      // 平滑压感（避免跳变）
      const smoothP = lastPressure.current * 0.6 + pressure * 0.4;
      lastPressure.current = smoothP;
      stampBrush(ctx, lastPos.current, pos, smoothP, brushType, brushSize, color);
      lastPos.current = pos;
      return;
    }

    if (tool === 'eraser') {
      const ctx = canvasRef.current.getContext('2d');
      ctx.lineWidth = brushSize * 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#FFFFFF';
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(pos.x, pos.y);
      lastPos.current = pos;
      return;
    }

    // 形状工具：在 overlay 上实时预览
    if (tool === 'line' || tool === 'rect' || tool === 'circle') {
      const ctx = overlayRef.current.getContext('2d');
      ctx.clearRect(0, 0, size.w, size.h);
      ctx.lineWidth = brushSize;
      ctx.lineCap = 'round';
      ctx.strokeStyle = color;
      ctx.fillStyle = color;

      const s = startPos.current;
      if (tool === 'line') {
        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(pos.x, pos.y);
        ctx.stroke();
      } else if (tool === 'rect') {
        if (fillShape) ctx.fillRect(s.x, s.y, pos.x - s.x, pos.y - s.y);
        else ctx.strokeRect(s.x, s.y, pos.x - s.x, pos.y - s.y);
      } else if (tool === 'circle') {
        const rx = Math.abs(pos.x - s.x) / 2;
        const ry = Math.abs(pos.y - s.y) / 2;
        const cx = (s.x + pos.x) / 2;
        const cy = (s.y + pos.y) / 2;
        ctx.beginPath();
        ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
        if (fillShape) ctx.fill();
        else ctx.stroke();
      }
    }
  }, [tool, brushType, color, brushSize, fillShape, size, getCanvasPos, stampBrush]);

  const handlePointerUp = useCallback(() => {
    if (!drawing.current) return;
    drawing.current = false;

    const canvas = canvasRef.current;
    const overlay = overlayRef.current;
    const ctx = canvas.getContext('2d');

    // 重置 compositeOperation
    ctx.globalCompositeOperation = 'source-over';

    // 形状工具：把 overlay 合并到主画布
    if (tool === 'line' || tool === 'rect' || tool === 'circle') {
      ctx.drawImage(overlay, 0, 0);
      overlay.getContext('2d').clearRect(0, 0, size.w, size.h);
    }

    saveSnapshot();
  }, [tool, size, saveSnapshot]);

  // 清空
  const clearCanvas = useCallback(() => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, size.w, size.h);
    saveSnapshot();
  }, [size, saveSnapshot]);

  // 保存
  const handleSave = useCallback(async () => {
    if (isSaving) return;
    setIsSaving(true);
    try {
      const dataUrl = canvasRef.current.toDataURL('image/png');
      const cosUrl = await uploadBase64ToCos(dataUrl);
      onSave?.(cosUrl);
    } catch (err) {
      console.error('草图保存失败:', err);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, onSave]);

  // 快捷键
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) { e.preventDefault(); undo(); }
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) { e.preventDefault(); redo(); }
      if (e.key === 'b') { setTool('brush'); }
      if (e.key === 'e') setTool('eraser');
      if (e.key === 'l') setTool('line');
      if (e.key === 'r') setTool('rect');
      if (e.key === 'o') setTool('circle');
      if (e.key === 't') setTool('text');
      if (e.key === '1') { setTool('brush'); setBrushType('pen'); }
      if (e.key === '2') { setTool('brush'); setBrushType('pencil'); }
      if (e.key === '3') { setTool('brush'); setBrushType('ink'); }
      if (e.key === '4') { setTool('brush'); setBrushType('marker'); }
      if (e.key === 'Escape') { if (showBrushMenu) setShowBrushMenu(false); else onClose?.(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, undo, redo, onClose, showBrushMenu]);

  if (!isOpen) return null;

  const accent = theme.colors.accentPrimary;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ backgroundColor: 'var(--overlay-heavy)', backdropFilter: 'blur(4px)' }}>
      <div className="flex flex-col rounded-2xl overflow-hidden shadow-2xl"
        style={{
          backgroundColor: 'var(--color-bg-secondary)',
          border: '1px solid var(--color-border-primary)',
          maxWidth: '95vw', maxHeight: '95vh',
        }}>

        {/* 顶部栏 */}
        <div className="flex items-center justify-between px-4 py-2"
          style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
          <div className="flex items-center gap-3">
            <span className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>
              {title || '草图编辑器'}
            </span>
            <span className="text-2xs px-2 py-0.5 rounded-full"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              {aspectRatio} · {size.w}×{size.h}
            </span>
          </div>

          <div className="flex items-center gap-2">
            {/* 当前笔刷类型标签 */}
            {tool === 'brush' && (
              <span className="text-2xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: `${accent}20`, color: accent }}>
                {BRUSH_TYPES.find(b => b.id === brushType)?.label}
              </span>
            )}

            {/* 颜色 */}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)}
              className="w-7 h-7 rounded cursor-pointer border-0" style={{ padding: 0 }} />

            {/* 笔刷大小 */}
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
              style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
              <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>粗细</span>
              <input type="range" min="1" max="50" value={brushSize}
                onChange={(e) => setBrushSize(Number(e.target.value))}
                className="w-20 h-1 accent-current" style={{ accentColor: accent }} />
              <span className="text-2xs w-5 text-center" style={{ color: 'var(--color-text-primary)' }}>{brushSize}</span>
            </div>

            {/* 填充 */}
            {(tool === 'rect' || tool === 'circle') && (
              <button onClick={() => setFillShape(!fillShape)}
                className="text-2xs px-2 py-1 rounded-lg transition-all"
                style={{
                  backgroundColor: fillShape ? accent : 'var(--color-bg-tertiary)',
                  color: fillShape ? 'white' : 'var(--color-text-muted)',
                }}>
                {fillShape ? '填充' : '描边'}
              </button>
            )}

            <div className="h-5 w-px mx-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />

            {/* 撤销/重做 */}
            <button onClick={undo} disabled={!canUndo} className="p-1.5 rounded-lg transition-all disabled:opacity-30"
              style={{ color: 'var(--color-text-secondary)' }} title="撤销 (Ctrl+Z)">
              <Undo2 size={16} />
            </button>
            <button onClick={redo} disabled={!canRedo} className="p-1.5 rounded-lg transition-all disabled:opacity-30"
              style={{ color: 'var(--color-text-secondary)' }} title="重做 (Ctrl+Shift+Z)">
              <Redo2 size={16} />
            </button>
            <button onClick={clearCanvas} className="p-1.5 rounded-lg transition-all hover:opacity-70"
              style={{ color: 'var(--color-text-muted)' }} title="清空画布">
              <Trash2 size={16} />
            </button>

            <div className="h-5 w-px mx-1" style={{ backgroundColor: 'var(--color-border-primary)' }} />

            <button onClick={onClose} className="p-1.5 rounded-lg transition-all hover:opacity-70"
              style={{ color: 'var(--color-text-muted)' }} title="关闭 (Esc)">
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* 左侧工具栏 */}
          <div className="flex flex-col gap-1 p-2"
            style={{ borderRight: '1px solid var(--color-border-primary)', backgroundColor: 'var(--color-bg-tertiary)' }}>
            {TOOLS.map(t => {
              const isBrush = t.id === 'brush';
              const active = isBrush ? tool === 'brush' : tool === t.id;
              // 画笔按钮显示当前笔刷图标
              const CurrentIcon = isBrush
                ? (BRUSH_TYPES.find(b => b.id === brushType)?.icon || Pencil)
                : t.icon;
              return (
                <div key={t.id} className="relative">
                  <button
                    onClick={() => {
                      if (isBrush) {
                        setTool('brush');
                        setShowBrushMenu(!showBrushMenu);
                      } else {
                        setTool(t.id);
                        setShowBrushMenu(false);
                      }
                    }}
                    title={isBrush ? `${t.label} (${BRUSH_TYPES.find(b => b.id === brushType)?.label})` : t.label}
                    className="w-9 h-9 flex items-center justify-center rounded-lg transition-all"
                    style={{
                      backgroundColor: active ? accent : 'transparent',
                      color: active ? 'white' : 'var(--color-text-secondary)',
                    }}>
                    <CurrentIcon size={16} />
                  </button>

                  {/* 笔刷子菜单 */}
                  {isBrush && showBrushMenu && (
                    <div className="absolute left-full top-0 ml-2 rounded-lg shadow-xl overflow-hidden z-50 animate-in fade-in zoom-in-95 duration-150"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        border: '1px solid var(--color-border-primary)',
                        minWidth: 140,
                      }}>
                      <div className="px-2 py-1.5 text-2xs font-bold"
                        style={{ color: 'var(--color-text-muted)', borderBottom: '1px solid var(--color-border-primary)' }}>
                        笔刷类型
                      </div>
                      {BRUSH_TYPES.map((b, i) => {
                        const BIcon = b.icon;
                        const isActive = brushType === b.id;
                        return (
                          <button key={b.id}
                            className="w-full px-3 py-1.5 flex items-center gap-2 text-left transition-all duration-200"
                            style={{
                              backgroundColor: isActive ? `${accent}20` : 'transparent',
                              color: isActive ? accent : 'var(--color-text-primary)',
                            }}
                            onMouseEnter={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)'; }}
                            onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.backgroundColor = 'transparent'; }}
                            onClick={() => {
                              setBrushType(b.id);
                              setTool('brush');
                              setShowBrushMenu(false);
                            }}>
                            <BIcon size={14} />
                            <div className="flex flex-col">
                              <span className="text-2xs font-medium">{b.label}</span>
                              <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>{b.desc} · {i + 1}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 画布区域 */}
          <div className="flex-1 flex items-center justify-center p-4"
            style={{ backgroundColor: 'var(--color-bg-primary)' }}>
            <div className="relative" style={{
              width: displaySize.w, height: displaySize.h,
              boxShadow: 'var(--shadow-modal)',
              borderRadius: 4,
            }}>
              <canvas ref={canvasRef}
                style={{ width: displaySize.w, height: displaySize.h, borderRadius: 4, position: 'absolute', top: 0, left: 0 }} />
              <canvas ref={overlayRef}
                style={{
                  width: displaySize.w, height: displaySize.h, borderRadius: 4,
                  position: 'absolute', top: 0, left: 0,
                  cursor: tool === 'eyedropper' ? 'crosshair' : tool === 'text' ? 'text' : 'crosshair',
                  touchAction: 'none',
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerLeave={handlePointerUp}
              />
            </div>
          </div>
        </div>

        {/* 底部操作栏 */}
        <div className="flex items-center justify-between gap-2 px-4 py-2"
          style={{ borderTop: '1px solid var(--color-border-primary)' }}>
          {/* 宽高比选择 */}
          <div className="flex items-center gap-1.5">
            <span className="text-2xs font-bold" style={{ color: 'var(--color-text-muted)' }}>比例</span>
            {ASPECT_RATIOS.map(r => (
              <button key={r} onClick={() => setAspectRatio(r)}
                className="px-2 py-0.5 rounded-full text-2xs font-semibold transition-all"
                style={{
                  backgroundColor: aspectRatio === r ? accent : 'var(--color-bg-tertiary)',
                  color: aspectRatio === r ? 'white' : 'var(--color-text-muted)',
                }}>
                {r}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-4 py-1.5 rounded-lg text-xs font-medium transition-all"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)' }}>
              取消
            </button>
            <button onClick={handleSave} disabled={isSaving}
              className="px-4 py-1.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50"
              style={{ backgroundColor: accent, color: 'white' }}>
              {isSaving ? '上传中...' : '保存草图'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SketchEditorModal;
